import { useEffect, useMemo, useRef, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn';
import { hcFillSelected } from '../../lib/forcedColors';
import { editPage, visibleEditPages } from '../brand/editPages';
import { editPagePath, type EditPageKey } from '../brand/paths';
import { useScrollSpy } from './useScrollSpy';
import { useStickyMaxHeight } from './useStickyMaxHeight';

// The entries look like R1's SettingsTabs (spec:306): the selected one as the Weekly slots list has it,
// and in forced colours a system fill. Below `lg` the page entries are a compact row that scrolls
// sideways, each at least a touch target high.
const ENTRY =
  'focus-ring block rounded-lg px-3 py-2 text-sm transition-colors max-lg:flex max-lg:min-h-[var(--touch-target)] max-lg:items-center max-lg:whitespace-nowrap';
const SECTION_ENTRY = 'focus-ring block rounded-lg px-3 py-1.5 text-sm transition-colors';
const ACTIVE = cn('bg-tint font-semibold text-tint-fg', hcFillSelected);
const IDLE = 'text-ink-label hover:bg-chip hover:text-ink';

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The Edit Brand Workspace side menu (spec:271-288, web.md §6.1): "Workspace settings", the four pages
 * (Danger zone hidden from members, E30), and under the open page its sections, the one nearest the top
 * of the page highlighted as the customer scrolls.
 *
 * - A page entry is a link: leaving a page with unsaved changes asks first (the page's guard).
 * - A section entry scrolls to its section (smoothly, unless the reader asks for less motion) and moves
 *   the focus there; the URL does not change.
 * - From `lg` it is sticky, 13.5rem wide, pinned at the scroll region's top padding, and scrolls on its
 *   own when it is taller than what the window shows, keeping the highlighted entry in view.
 * - Below `lg` it is a compact row of the page entries above the content, scrolling sideways.
 */
export function WorkspaceSideMenu({
  page,
  slug,
  canEdit,
}: {
  page: EditPageKey;
  /** The workspace's slug: every entry keeps `?ws=`. */
  slug: string;
  canEdit: boolean;
}) {
  const navRef = useRef<HTMLElement>(null);
  const rowRef = useRef<HTMLUListElement>(null);
  const pages = visibleEditPages(canEdit);
  const sections = editPage(page).sections;
  const ids = useMemo(() => sections.map((section) => section.id), [sections]);
  const { activeId, hold } = useScrollSpy(ids, navRef);
  useStickyMaxHeight(navRef);

  // Keeps the highlighted entry in view when the menu scrolls on its own (a short window), and the
  // open page's entry in view in the narrow row. Moves the menu's own scroll only, never the page's
  // (scrollIntoView would move both).
  useEffect(() => {
    const nav = navRef.current;
    if (nav && nav.scrollHeight > nav.clientHeight) {
      const entry =
        nav.querySelector<HTMLElement>('[aria-current="location"]') ?? nav.querySelector<HTMLElement>('[aria-current="page"]');
      if (entry) {
        const top = entry.getBoundingClientRect().top - nav.getBoundingClientRect().top + nav.scrollTop;
        const bottom = top + entry.getBoundingClientRect().height;
        if (top < nav.scrollTop) nav.scrollTop = top;
        else if (bottom > nav.scrollTop + nav.clientHeight) nav.scrollTop = bottom - nav.clientHeight;
      }
    }
    const row = rowRef.current;
    if (row && row.scrollWidth > row.clientWidth) {
      const entry = row.querySelector<HTMLElement>('[aria-current="page"]');
      if (entry) {
        const left = entry.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft;
        const right = left + entry.getBoundingClientRect().width;
        if (left < row.scrollLeft) row.scrollLeft = left;
        else if (right > row.scrollLeft + row.clientWidth) row.scrollLeft = right - row.clientWidth;
      }
    }
  }, [page, activeId]);

  const goToSection = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    const section = document.getElementById(id);
    if (!section) return;
    hold(id);
    section.scrollIntoView?.({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    section.focus({ preventScroll: true });
  };

  return (
    <nav
      ref={navRef}
      aria-label="Workspace pages"
      className="p-1 lg:sticky lg:top-0 lg:w-[13.5rem] lg:shrink-0 lg:self-start lg:overflow-y-auto"
    >
      <h2 className="mb-1 hidden px-3 pt-1 text-sm font-semibold text-ink-strong lg:block">Workspace settings</h2>
      <ul ref={rowRef} className="flex gap-1 overflow-x-auto p-0.5 lg:block lg:space-y-0.5 lg:overflow-visible lg:p-0">
        {pages.map((entry) => {
          const open = entry.key === page;
          return (
            <li key={entry.key} className="shrink-0">
              <Link
                to={editPagePath(entry.key, slug)}
                aria-current={open ? 'page' : undefined}
                className={cn(ENTRY, open ? ACTIVE : IDLE)}
              >
                {entry.label}
              </Link>
              {open && entry.sections.length > 0 && (
                <ul className="ml-3 mt-0.5 hidden space-y-0.5 border-l border-line pl-3 lg:block">
                  {entry.sections.map((section) => {
                    const current = activeId === section.id;
                    return (
                      <li key={section.id}>
                        <a
                          href={`#${section.id}`}
                          aria-current={current ? 'location' : undefined}
                          onClick={(event) => goToSection(event, section.id)}
                          className={cn(SECTION_ENTRY, current ? ACTIVE : IDLE)}
                        >
                          {section.label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
